/**
 * Dev-only MapLibre fill overlay for the stop preload / prefetch bbox (blue).
 */

import type { FeatureCollection } from "geojson";
import type { GeoJSONSource, Map as MapLibre } from "maplibre-gl";

import { STOPS_LAYER_PARENTS_ID } from "../mapLayerIds";
import type { StopBbox } from "../stops/stopPreload.types";
import { stopBboxToPolygonFeatureCollection } from "../stops/stopPreloadBbox.tools";
import {
    STOPS_DEBUG_BBOX_FILL_LAYER_ID,
    STOPS_DEBUG_BBOX_LINE_LAYER_ID,
    STOPS_DEBUG_BBOX_SOURCE_ID,
} from "./mapDebugLayerIds";

/** Fill color for preload bbox debug polygon. */
const STOPS_DEBUG_BBOX_FILL_COLOR = "#3b82f6";

/** Fill opacity for preload bbox debug polygon. */
const STOPS_DEBUG_BBOX_FILL_OPACITY = 0.15;

/** Line color for preload bbox debug outline. */
const STOPS_DEBUG_BBOX_LINE_COLOR = "#3b82f6";

/** Line opacity for preload bbox debug outline. */
const STOPS_DEBUG_BBOX_LINE_OPACITY = 0.5;

const EMPTY_FEATURE_COLLECTION: FeatureCollection = {
    "type":     "FeatureCollection",
    "features": [],
};

/**
 * Resolves insertion anchor below stop symbol layers when available.
 *
 * @param map Target MapLibre map.
 * @returns Layer id to pass as `beforeId`, or undefined.
 */
const resolveStopDebugBeforeId = ( map: MapLibre ): string | undefined => (
    map.getLayer( STOPS_LAYER_PARENTS_ID ) != null ? STOPS_LAYER_PARENTS_ID : undefined
);

/**
 * Ensures GeoJSON source and fill/line layers for stop preload bbox debug exist.
 *
 * @param map Target MapLibre map.
 */
export const ensureStopsPreloadBboxDebugLayers = ( map: MapLibre ): void => {
    if ( map.getSource( STOPS_DEBUG_BBOX_SOURCE_ID ) === undefined ) {
        map.addSource( STOPS_DEBUG_BBOX_SOURCE_ID, {
            "type": "geojson",
            "data": EMPTY_FEATURE_COLLECTION,
        } );
    }

    const beforeId = resolveStopDebugBeforeId( map );

    if ( map.getLayer( STOPS_DEBUG_BBOX_FILL_LAYER_ID ) === undefined ) {
        map.addLayer( {
            "id":     STOPS_DEBUG_BBOX_FILL_LAYER_ID,
            "type":   "fill",
            "source": STOPS_DEBUG_BBOX_SOURCE_ID,
            "paint":  {
                "fill-color":         STOPS_DEBUG_BBOX_FILL_COLOR,
                "fill-opacity":       STOPS_DEBUG_BBOX_FILL_OPACITY,
                "fill-outline-color": STOPS_DEBUG_BBOX_LINE_COLOR,
            },
        }, beforeId );
    }

    if ( map.getLayer( STOPS_DEBUG_BBOX_LINE_LAYER_ID ) === undefined ) {
        map.addLayer( {
            "id":     STOPS_DEBUG_BBOX_LINE_LAYER_ID,
            "type":   "line",
            "source": STOPS_DEBUG_BBOX_SOURCE_ID,
            "paint":  {
                "line-color":   STOPS_DEBUG_BBOX_LINE_COLOR,
                "line-opacity": STOPS_DEBUG_BBOX_LINE_OPACITY,
                "line-width":   2,
            },
        }, beforeId );
    }
};

/**
 * Updates the stop preload bbox debug polygon.
 *
 * @param map Target MapLibre map.
 * @param bbox Bbox to draw, or null to clear.
 */
export const updateStopsPreloadBboxDebugLayer = ( map: MapLibre, bbox: StopBbox | null ): void => {
    const source = map.getSource( STOPS_DEBUG_BBOX_SOURCE_ID );
    if ( source === undefined || !( "setData" in source ) ) {
        return;
    }

    const data = bbox === null
        ? EMPTY_FEATURE_COLLECTION
        : stopBboxToPolygonFeatureCollection( bbox );

    ( source as GeoJSONSource ).setData( data );
};

/**
 * Removes stop preload bbox debug source and layers.
 *
 * @param map Target MapLibre map.
 */
export const removeStopsPreloadBboxDebugLayers = ( map: MapLibre ): void => {
    if ( map.getLayer( STOPS_DEBUG_BBOX_LINE_LAYER_ID ) != null ) {
        map.removeLayer( STOPS_DEBUG_BBOX_LINE_LAYER_ID );
    }

    if ( map.getLayer( STOPS_DEBUG_BBOX_FILL_LAYER_ID ) != null ) {
        map.removeLayer( STOPS_DEBUG_BBOX_FILL_LAYER_ID );
    }

    if ( map.getSource( STOPS_DEBUG_BBOX_SOURCE_ID ) != null ) {
        map.removeSource( STOPS_DEBUG_BBOX_SOURCE_ID );
    }
};
